/**
 * Season entity class
 */
import moment from 'moment'
import { timeStringToDate, formatDateByTimezone } from 'app/helpers/date'

/**
 * SeasonFactory
 * returns Season entity class
 *
 * @class SeasonFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Season} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SeasonFactory
 */
/* @ngInject */
export default function SeasonFactory ($http, Entity, ShowSet, Network, FileUploadMixin, onOffMixin, alertLiteSvc) {
  /* === Season class === */

  /**
   * Season model
   *
   * @class Season
   * @classdesc Season model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @return {models.Season} Season entity
   */
  function Season () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Season.prototype = Object.create(Entity.prototype)
  Season.prototype.constructor = Season

  /* === Season instance methods & fields === */

  Object.assign(Season.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Season#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      description: String,
      directors: String,
      stars: String,
      year: Number,
      $releasedDate: Date,
      enabled: Boolean,
      sortOrder: Number,
      displayNumber: String,
      portraitImageUrl: String,
      backgroundImageUrl: String,
      publishTime: String,
      unpublishTime: String,
      newEpisodePosition: String,
      tvShow: {
        id: Number
      },
      contentSets: ['ShowSet'],
      assignedSubscriptions: ['Subscription'],
      $scheduledPublishing: Boolean,
      $episodes: [],
      $fromDate: Date,
      $fromTime: Date,
      $toDate: Date,
      $toTime: Date,
      $toDateUnlimited: true,
      $itemType: 'season',
      $itemTypeForContentSet: 'show',
      $contentSetName: 'Show Set',
      $contentSetType: 'showSet'
    },

    /**
     * url template
     *
     * @memberof models.Season#
     * @type {String}
     */
    _urlTemplate: 'api/tvshows/{{::tvShow.id}}/seasons/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Season#
     * @type {String}
     */
    _publicUrlTemplate: 'shows/{{::tvShow.id}}/seasons/{{::id}}',

    /**
     * Prepare object
     *
     * @memberof models.Season#
     * @override
     */
    _prepare: function () {
      /* Note: set default values. */
      const date = new Date()
      this.$releasedDate = this.year
        ? date.setFullYear(this.year)
        : date
      this.$episodes = []

      /* Note: set default values. */
      this.$scheduledPublishing = !!this.publishTime
      this.$toDateUnlimited = !this.unpublishTime

      this.$fromDate = this.publishTime ? formatDateByTimezone(this.publishTime, false).toDate() : moment().toDate()
      this.$toDate = this.unpublishTime ? formatDateByTimezone(this.unpublishTime, false).toDate() : this.$fromDate
      this.$fromTime = timeStringToDate(this.publishTime ? moment.utc(this.publishTime).format('HH:mm') : null, 'startOfDay')
      this.$toTime = timeStringToDate(this.unpublishTime ? moment.utc(this.unpublishTime).format('HH:mm') : null, 'endOfDay')
    },

    /**
     * Get underlying object
     *
     * @memberof models.Season#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      const releasedDate = new Date(this.$releasedDate)
      this.year = releasedDate.getFullYear()
      const fromDate = this.$fromDate.setHours(this.$fromTime.getHours(), this.$fromTime.getMinutes(), 0)
      const toDate = this.$toDate.setHours(this.$toTime.getHours(), this.$toTime.getMinutes(), 0)
      this.publishTime = formatDateByTimezone(fromDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.unpublishTime = formatDateByTimezone(toDate).format('YYYY-MM-DDTHH:mm:ss[Z]')

      if (this.$toDateUnlimited) {
        this.unpublishTime = null
      }

      if (!this.$scheduledPublishing) {
        this.publishTime = null
        this.unpublishTime = null
      }

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get image URL for given size
     *
     * @memberof models.Season#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}') !== -1
          ? this[fieldName].replace('{{size}}', size)
          : this[fieldName] + '?resize=' + size
        : ''
    }

  }, onOffMixin, FileUploadMixin)

  /* === Season static methods & fields === */

  /**
   * Load Content sets
   *
   * @param show
   * @param id
   * @returns {*}
   */
  Season.loadAvailableContentSets = function (show, id) {
    return $http.get(`/nora/api/tvshows/${show.id}/seasons/${id}/extra/content-sets`)
      .data()
      .then(data => data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(Season, Entity)
  Season._name = 'Season'
  Season.init()

  // permissions
  Season.access = {
    view: 'viewVodSeries',
    create: 'createVodSeries',
    edit: 'editVodSeries',
    delete: 'deleteVodSeries'
  }

  return Season
}
